
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye, FaPaperPlane, FaTrash, FaPrint, FaFileExcel, FaEdit, FaPaperclip, FaFileAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  getPaymentVouchers,
  getPaymentVoucherDetails,
  postPaymentVoucher,
  deletePaymentVoucher,
  getPostingAccounts,
  getBanksAndSafes,
  getProjects,
  clearError,
  clearSuccess,
} from '../../../../redux/Slices/authSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { useToastNotifications } from '../../../../shared/hooks/useToastNotifications';
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from '../Reports/components/PdfPreviewModal';
import { calculatePdfPagination, convertArabicNumbersToEnglish, BASE_URL } from '../../../../shared/utils.js/utils';

const getProjectDisplayName = (voucher, isRTL) => {
  if (!voucher) return '—';
  return voucher.project_name
    || (isRTL
      ? (voucher.project?.name_ar || voucher.project?.name || voucher.project?.name_en)
      : (voucher.project?.name_en || voucher.project?.name || voucher.project?.name_ar))
    || '—';
};

const getReceivedFromDisplayName = (voucher, isRTL) => {
  if (!voucher) return '—';
  if (voucher.received_from_name) return voucher.received_from_name;

  const firstDetailAccount = voucher.details?.[0]?.account;
  if (!firstDetailAccount) return '—';

  return isRTL
    ? (firstDetailAccount.name_ar || firstDetailAccount.name || firstDetailAccount.name_en || '—')
    : (firstDetailAccount.name_en || firstDetailAccount.name || firstDetailAccount.name_ar || '—');
};

const normalizeDateForApi = (value) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return undefined;
  const day = match[1].padStart(2, '0');
  const month = match[2].padStart(2, '0');
  const year = match[3];
  return `${year}-${month}-${day}`;
};

const STATUS_FILTER_ENUM = Object.freeze({
  DRAFT: 'draft',
  POSTED: 'posted',
});

const PaymentVouchers = () => {
  const handleEdit = (voucher) => {
    navigate(`/payment-vouchers/edit/${voucher.id}`);
  }
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const {
    paymentVouchers,
    paymentVouchersPagination,
    postingAccounts,
    banksAndSafes,
    projects,
    projectsPagination,
    isLoading,
    error,
    success,
  } = useSelector((state) => state.auth);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [projectId, setProjectId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectCurrentPage, setProjectCurrentPage] = useState(1);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [voucherToDelete, setVoucherToDelete] = useState(null);
  const [voucherForPrint, setVoucherForPrint] = useState(null);
  const [voucherForPreview, setVoucherForPreview] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [allVouchersForPDF, setAllVouchersForPDF] = useState([]);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [allPaymentVouchers, setAllPaymentVouchers] = useState([]);
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const { t, i18n: i18nHook } = useTranslation('global');
  const isRTL = i18nHook.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const projectSearchTimeoutRef = useRef(null);
  const projectSearchInputRef = useRef(null);
  const searchDebounceTimeoutRef = useRef(null);
  const vouchersThrottleTimeoutRef = useRef(null);
  const vouchersLastDispatchRef = useRef(0);
  const VOUCHERS_THROTTLE_MS = 500;

  const accountsOptions = useMemo(() => {
    const posting = Array.isArray(postingAccounts) ? postingAccounts : [];
    const banks = Array.isArray(banksAndSafes) ? banksAndSafes : [];
    const merged = [...banks, ...posting];
    const uniqueById = new Map();
    merged.forEach((acc) => {
      const id = String(acc?.id ?? acc?.account_id ?? '');
      if (id && !uniqueById.has(id)) uniqueById.set(id, acc);
    });
    return Array.from(uniqueById.values());
  }, [postingAccounts, banksAndSafes]);

  const filteredAccounts = useMemo(() => {
    const term = accountSearchTerm.replace(/\s+/g, ' ').trim().toLowerCase();
    if (!term) return accountsOptions;
    return accountsOptions.filter((acc) => {
      const nameAr = String(acc?.name_ar || '').toLowerCase();
      const nameEn = String(acc?.name_en || '').toLowerCase();
      const name = String(acc?.name || '').toLowerCase();
      const code = String(acc?.code || '').toLowerCase();
      return nameAr.includes(term) || nameEn.includes(term) || name.includes(term) || code.includes(term);
    });
  }, [accountsOptions, accountSearchTerm]);

  useEffect(() => {
    setTitle('sidenav.paymentVouchers');
  }, [setTitle]);

  useEffect(() => {
    dispatch(getPostingAccounts());
    dispatch(getBanksAndSafes());
  }, [dispatch]);

  useEffect(() => {
    if (!projectDropdownOpen) return;
    dispatch(getProjects({ page: projectCurrentPage, per_page: 10, search: projectSearchTerm || undefined }));
  }, [dispatch, projectDropdownOpen, projectCurrentPage, projectSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown-container]')) {
        setProjectDropdownOpen(false);
        setAccountDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buildVoucherFilters = (searchValue = debouncedSearch) => {
    const normalisedSearch = convertArabicNumbersToEnglish(searchValue).replace(/\s+/g, ' ').trim();
    return {
      page: currentPage,
      per_page: perPage,
      // Use undefined (not '') so the slice skips the param entirely when empty
      search: normalisedSearch || undefined,
      project_id: projectId.trim() || undefined,
      account_id: accountId.trim() || undefined,
      status: Object.values(STATUS_FILTER_ENUM).includes(statusFilter) ? statusFilter : undefined,
      date_from: normalizeDateForApi(dateFrom),   // returns undefined when empty
      date_to: normalizeDateForApi(dateTo),      // returns undefined when empty
      sort_by: sortField,
      sort_order: sortOrder,
    };
  };

  useEffect(() => {
    if (searchDebounceTimeoutRef.current) clearTimeout(searchDebounceTimeoutRef.current);
    searchDebounceTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearch(search);
    }, 400);
    return () => {
      if (searchDebounceTimeoutRef.current) clearTimeout(searchDebounceTimeoutRef.current);
    };
  }, [search]);

  const throttledFetchVouchers = (filters) => {
    const now = Date.now();
    const elapsed = now - vouchersLastDispatchRef.current;
    const remaining = Math.max(VOUCHERS_THROTTLE_MS - elapsed, 0);

    if (vouchersThrottleTimeoutRef.current) clearTimeout(vouchersThrottleTimeoutRef.current);

    if (remaining === 0) {
      vouchersLastDispatchRef.current = Date.now();
      dispatch(getPaymentVouchers(filters));
      return;
    }

    vouchersThrottleTimeoutRef.current = setTimeout(() => {
      vouchersLastDispatchRef.current = Date.now();
      dispatch(getPaymentVouchers(filters));
    }, remaining);
  };

  useEffect(() => {
    throttledFetchVouchers(buildVoucherFilters());
    return () => {
      if (vouchersThrottleTimeoutRef.current) clearTimeout(vouchersThrottleTimeoutRef.current);
    };
  }, [dispatch, currentPage, perPage, debouncedSearch, projectId, accountId, statusFilter, dateFrom, dateTo, sortField, sortOrder]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (paymentVouchers && Array.isArray(paymentVouchers)) {
      const total = paymentVouchersPagination?.total || paymentVouchers.length;
      const currentPageSafe = Math.max(1, currentPage);
      const startIndex = (currentPageSafe - 1) * perPage;
      const listWithSerials = paymentVouchers.map((voucher, idx) => ({
        ...voucher,
        _serialNumber: sortOrder === 'desc'
          ? total - (startIndex + idx)
          : startIndex + idx + 1
      }));
      setAllPaymentVouchers(listWithSerials);
    }
  }, [paymentVouchers, paymentVouchersPagination, currentPage, perPage, sortOrder]);

  const sortVouchersList = (list) => {
    return list;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const sortedPaymentVouchers = allPaymentVouchers;

  // استخدام الـ hook الموحد لمعالجة التوستات
  useToastNotifications({
    success,
    error,
    onSuccess: () => {
      // Only refresh when adding new voucher
      if (success && (success.includes('إضافة') || success.includes('تم إضافة') || success.includes('Add') || success.includes('added'))) {
        dispatch(getPaymentVouchers(buildVoucherFilters(search)));
      }
      // Don't refresh on delete - it updates optimistically
    }
  });

  const handleApplyPerPage = () => {
    const validValue = Math.max(1, Math.min(perPageInput, 1000));
    setPerPage(validValue);
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setDebouncedSearch(search); // triggers useEffect → single API call with all current filters
    // No direct dispatch here — the useEffect watches debouncedSearch and fires once
  };

  const resetFilters = () => {
    setSearch('');
    setDebouncedSearch(''); // reset debounced value too — otherwise old search stays active
    setProjectId('');
    setAccountId('');
    setProjectSearchTerm('');
    setAccountSearchTerm('');
    setProjectCurrentPage(1);
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const renderAccountDisplay = (id) => {
    if (!id) return t('paymentVouchers.form.selectAccount');
    const selected = accountsOptions.find((acc) => String(acc?.id ?? acc?.account_id) === String(id));
    if (!selected) return t('paymentVouchers.form.selectAccount');
    const name = isRTL
      ? (selected.name_ar || selected.name || selected.name_en || '')
      : (selected.name_en || selected.name || selected.name_ar || '');
    return `${selected.code ? `${selected.code} - ` : ''}${name}`;
  };

  const renderProjectDisplay = (id) => {
    if (!id) return t('paymentVouchers.form.selectProject', { defaultValue: 'Select Project' });
    const selected = (projects || []).find((p) => String(p.id) === String(id));
    if (!selected) return t('paymentVouchers.form.selectProject', { defaultValue: 'Select Project' });
    const name = isRTL ? (selected.name_ar || selected.name_en || '') : (selected.name_en || selected.name_ar || '');
    return `${selected.code ? `${selected.code} - ` : ''}${name}`;
  };

  const handleProjectSearchChange = (value) => {
    const normalized = value.replace(/\s+/g, ' ').trim();
    setProjectSearchTerm(value);
    setProjectCurrentPage(1);
    if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current);
    projectSearchTimeoutRef.current = setTimeout(() => {
      dispatch(getProjects({ page: 1, per_page: 10, search: normalized || undefined }));
    }, 300);
  };

  const openDeleteModal = (voucher) => {
    setVoucherToDelete(voucher);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setVoucherToDelete(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!voucherToDelete) return;
    const idToDelete = voucherToDelete.id;

    // Don't do optimistic delete - we can't know if voucher is linked to another system
    // Only remove from local state after successful deletion
    closeDeleteModal();
    setVoucherToDelete(null);

    try {
      const result = await dispatch(deletePaymentVoucher(idToDelete));
      // Check if the action was rejected
      if (deletePaymentVoucher.rejected.match(result)) {
        throw new Error(result.payload || 'فشل حذف سند الصرف');
      }
      // Remove from local state after successful deletion
      setAllPaymentVouchers(prev => prev.filter(voucher => voucher.id !== idToDelete));
      // Error will be shown by useToastNotifications if needed
    } catch (error) {
      // Don't show error here - useToastNotifications will handle it
      // Voucher remains in the list on error
    }
  };

  const handlePost = (id) => {
    dispatch(postPaymentVoucher(id));
  };

  const openAttachmentsModal = (attachments) => {
    const safeAttachments = Array.isArray(attachments) ? attachments : [];
    setSelectedAttachments(safeAttachments);
    setAttachmentsModalOpen(true);
  };

  const closeAttachmentsModal = () => {
    setAttachmentsModalOpen(false);
    setSelectedAttachments([]);
  };

  const formatVoucherDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const locale = 'en-US';
      return convertArabicNumbersToEnglish(date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }));
    } catch {
      return convertArabicNumbersToEnglish(dateString);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0.00';
    const locale = 'en-US';
    return convertArabicNumbersToEnglish(parseFloat(amount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };

  // Handle print single payment voucher
  const handlePrintPaymentVoucher = async (voucher) => {
    try {
      let voucherData = voucher;
      if (!voucher.details || !voucher.account) {
        try {
          const result = await dispatch(getPaymentVoucherDetails(voucher.id)).unwrap();
          voucherData = result?.data || result;
        } catch (error) {
          console.error('Error fetching voucher details:', error);
        }
      }

      setVoucherForPrint(voucherData);
      setPdfType('single');
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      // Preload images
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo)
      ]);
      await new Promise((resolve) => setTimeout(resolve, 800));

      setIsGeneratingPdf(true);

      // Retry mechanism for element
      let element = null;
      let retries = 0;
      const maxRetries = 15;

      while (!element && retries < maxRetries) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        element = document.getElementById(`payment-voucher-pdf-${voucherData.id}`);
        if (!element) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          retries++;
        }
      }

      if (!element) {
        toast.error(t('paymentVouchers.print.error'), { rtl: isRTL });
        setIsGeneratingPdf(false);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 200));

      // Shared PDF Engine — capture + assembly
      const identifier = voucherData.reference || voucherData.voucher_number || voucherData.id;
      await generateSingleElementPdf({
        elementId: element.id,
        fileName: `ACE_PaymentVoucher_${identifier}_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
    } catch (err) {
      console.error('Payment Voucher PDF error:', err);
      toast.error(t('paymentVouchers.print.error'), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setVoucherForPrint(null);
      setPdfType(null);
    }
  };

  // Preload image helper
  const preloadImage = (src) =>
    new Promise((resolve) => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  /**
   * buildDynamicPdfChunks — uses the shared calculatePdfPagination engine from utils.js
   *
   * This function delegates all pagination math to the project-wide PDF engine so that
   * ALL reports (current and future) benefit from the same logic automatically.
   *
   * Page layout reference (A4 @ 96 dpi):
   *   Inner bordered div height  = 1033px  (height: '1033px' in template)
   *   Inner top padding          =   18px
   *   Inner bottom padding       =   10px
   *   Footer placeholder height  =  120px  (matches paddingBottom: '120px' in template)
   *   Safety margin              =   15px
   *
   * DO NOT add rowsPerPage / MAX_ROWS / fixed counts here.
   * Row fitting is computed per-row using actual DOM offsetHeight measurements.
   */
  const buildDynamicPdfChunks = (vouchers) => {
    if (!Array.isArray(vouchers) || vouchers.length === 0) {
      setPdfChunks([]);
      return [];
    }

    const elements = {
      topHeader: document.getElementById('payment-vouchers-pdf-measure-top-header'),
      thead: document.getElementById('payment-vouchers-pdf-measure-thead'),
      footer: document.getElementById('payment-vouchers-pdf-measure-footer'),
      tbody: document.getElementById('payment-vouchers-pdf-measure-tbody'),
    };

    // Delegate to the shared PDF pagination engine (calculatePdfPagination in shared/utils)
    // fallbackSize is estimated dynamically from page geometry — no magic row count
    const estimatedRowHeight = 30;
    const estimatedAvailable = 1033 - 18 - 10 - 180 - 120 - 15; // ≈ 700px
    const dynamicFallbackSize = Math.max(5, Math.floor(estimatedAvailable / estimatedRowHeight));

    const chunks = calculatePdfPagination(vouchers, elements, {
      pageHeight: 1033, // inner bordered div height (matches template)
      containerTopPadding: 18, // padding-top of inner content div
      containerBottomPadding: 10, // padding-bottom of inner content div
      safeMargin: 15, // gap between last row and footer start
      fallbackSize: dynamicFallbackSize, // geometry-based, NOT a fixed magic number
      defaultHeaderHeight: 180,
      defaultTableHeaderHeight: 32,
      defaultFooterHeight: 120, // matches paddingBottom: '120px' in template
      defaultRowHeight: 28,
    });

    setPdfChunks(chunks);
    return chunks;
  };

  const prepareAllVouchersPdfData = async () => {
    const allVouchers = await fetchAllPaymentVouchersForPDF();
    if (!allVouchers || allVouchers.length === 0) {
      toast.error(t('paymentVouchers.print.noData', { defaultValue: 'لا توجد بيانات متاحة' }), { rtl: isRTL });
      return [];
    }

    const sortedAllVouchers = sortVouchersList(allVouchers);
    setAllVouchersForPDF(sortedAllVouchers);

    await Promise.all([
      preloadImage(`${publicUrl}/HeaderAce.png`),
      preloadImage(`${publicUrl}/FooterAce.png`),
      preloadImage(headerLogo)
    ]);

    await new Promise((resolve) => setTimeout(resolve, 200));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));

    return buildDynamicPdfChunks(sortedAllVouchers);
  };

  // Handle preview all payment vouchers
  const handlePreviewAll = async () => {
    try {
      setIsLoadingDetails(true);
      const chunks = await prepareAllVouchersPdfData();
      if (!chunks || chunks.length === 0) return;
      setIsPreviewOpen(true);
    } catch (err) {
      console.error('Error fetching all vouchers for preview:', err);
      toast.error(t('paymentVouchers.print.error'), { rtl: isRTL });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Handle preview single payment voucher
  const handlePreviewSingle = async (voucher) => {
    try {
      setIsLoadingDetails(true);
      const result = await dispatch(getPaymentVoucherDetails(voucher.id)).unwrap();
      const voucherData = result?.data || result;
      setVoucherForPreview(voucherData);
      setIsPreviewOpen(true);
    } catch (error) {
      console.error('Error loading payment voucher details:', error);
      toast.error(t('paymentVouchers.preview.error', { defaultValue: 'حدث خطأ أثناء تحميل تفاصيل السند' }), { rtl: isRTL });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Handle print all payment vouchers
  const handlePrintAll = async () => {
    try {
      setIsGeneratingPdf(true);
      // Always re-fetch data for print to ensure we have the latest filtered data.
      // (pdfChunks from a previous preview may reflect different filters.)
      const chunks = await prepareAllVouchersPdfData();
      if (!chunks || chunks.length === 0) {
        setIsGeneratingPdf(false);
        return;
      }

      // Ensure pdfType is set so hidden templates remain rendered (belt-and-suspenders)
      setPdfType('all');

      // Wait for React to commit the state changes and paint the hidden templates
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      // Preload images
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo)
      ]);
      await new Promise((resolve) => setTimeout(resolve, 800));

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = chunks.map((_, i) => `payment-vouchers-pdf-page-${i}`);
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_PaymentVouchers_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('paymentVouchers.print.success', { defaultValue: 'تم تحميل ملف PDF بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Payment Vouchers PDF error:', error);
      toast.error(error.message || t('paymentVouchers.print.error'), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
    }
  };

  const activeFiltersForPdf = useMemo(() => {
    const parts = [];
    if (debouncedSearch) parts.push(`${t('common.search', { defaultValue: 'بحث' })}: ${debouncedSearch}`);
    if (projectId) {
      const proj = (projects || []).find(p => String(p.id) === String(projectId));
      const projName = proj ? (isRTL ? (proj.name_ar || proj.name_en) : (proj.name_en || proj.name_ar)) : projectId;
      parts.push(`${t('paymentVouchers.form.project', { defaultValue: 'المشروع' })}: ${projName}`);
    }
    if (accountId) {
      const acc = accountsOptions.find(a => String(a?.id ?? a?.account_id) === String(accountId));
      const accName = acc ? `${acc.code ? acc.code + ' - ' : ''}${isRTL ? (acc.name_ar || acc.name) : (acc.name_en || acc.name)}` : accountId;
      parts.push(`${t('paymentVouchers.table.account', { defaultValue: 'الحساب' })}: ${accName}`);
    }
    if (statusFilter) parts.push(`${t('paymentVouchers.table.status', { defaultValue: 'الحالة' })}: ${t(`paymentVouchers.status.${statusFilter}`, { defaultValue: statusFilter })}`);
    if (dateFrom) parts.push(`${t('common.from', { defaultValue: 'من' })}: ${dateFrom}`);
    if (dateTo) parts.push(`${t('common.to', { defaultValue: 'إلى' })}: ${dateTo}`);
    return parts.join(' | ');
  }, [debouncedSearch, projectId, accountId, statusFilter, dateFrom, dateTo, projects, accountsOptions, isRTL, t]);

  // Fetch all payment vouchers for PDF/Excel templates (per_page = total)
  const fetchAllPaymentVouchersForPDF = async () => {
    try {
      const params = {
        ...buildVoucherFilters(),
        page: 1,
        per_page: 10
      };

      const result = await dispatch(getPaymentVouchers(params));
      if (getPaymentVouchers.fulfilled && result.type === getPaymentVouchers.fulfilled.type) {
        const response = result.payload;
        const data = response?.data || response;
        const paginationData = data?.meta || data?.pagination || paymentVouchersPagination || null;
        const total = paginationData?.total || 0;

        if (total > 0) {
          const allParams = {
            ...buildVoucherFilters(),
            page: 1,
            per_page: total
          };

          const allResult = await dispatch(getPaymentVouchers(allParams));
          if (getPaymentVouchers.fulfilled && allResult.type === getPaymentVouchers.fulfilled.type) {
            const allResponse = allResult.payload;
            const allData = allResponse?.data || allResponse;
            let vouchersList = [];
            if (Array.isArray(allData?.data)) {
              vouchersList = allData.data;
            } else if (Array.isArray(allData)) {
              vouchersList = allData;
            }
            const totalVal = vouchersList.length;
            const listWithSerials = vouchersList.map((voucher, idx) => ({
              ...voucher,
              _serialNumber: sortOrder === 'desc'
                ? totalVal - idx
                : idx + 1
            }));
            setAllVouchersForPDF(listWithSerials);
            return listWithSerials;
          }
        }
      }
      return [];
    } catch (err) {
      console.error('Error fetching all payment vouchers:', err);
      return [];
    }
  };

  // Handle export single payment voucher to Excel
  const handleExportPaymentVoucherToExcel = (voucher) => {
    const headerSerial = t('paymentVouchers.table.serialNumber', { defaultValue: '#' });
    const headerDate = t('paymentVouchers.table.date', { defaultValue: 'التاريخ' });
    const headerReference = t('paymentVouchers.table.reference', { defaultValue: 'المرجع' });
    const headerAccount = t('paymentVouchers.table.account', { defaultValue: 'الحساب' });
    const headerReceivedFrom = t('paymentVouchers.table.receivedFromName', { defaultValue: 'مستلم من' });
    const headerProject = t('paymentVouchers.table.project', { defaultValue: 'المشروع' });
    const headerAmount = t('paymentVouchers.table.amount', { defaultValue: 'المبلغ' });
    const headerStatus = t('paymentVouchers.table.status', { defaultValue: 'الحالة' });
    const headerCreatedBy = t('paymentVouchers.table.createdBy', { defaultValue: 'بواسطة' });
    const headerPostedDate = t('paymentVouchers.table.postedDate', { defaultValue: 'تاريخ الترحيل' });

    const data = [{
      [headerSerial]: voucher._serialNumber || 1,
      [headerDate]: formatVoucherDate(voucher.voucher_date || voucher.date) || '—',
      [headerReference]: voucher.reference || voucher.voucher_number || voucher.id || '—',
      [headerAccount]: voucher.account
        ? `${voucher.account.code ? `${voucher.account.code} - ` : ''}${isRTL ? (voucher.account.name_ar || voucher.account.name || '') : (voucher.account.name_en || voucher.account.name || voucher.account.name_ar || '')}`
        : '—',
      [headerReceivedFrom]: getReceivedFromDisplayName(voucher, isRTL),
      [headerProject]: getProjectDisplayName(voucher, isRTL),
      [headerAmount]: Number(voucher.total_amount || voucher.amount || 0),
      [headerStatus]: statusLabel(voucher.status),
      [headerCreatedBy]: voucher.created_by || voucher.creator || '—',
      [headerPostedDate]: formatVoucherDate(voucher.posted_at) || '—',
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'سند صرف' : 'Payment Voucher');
    const identifier = voucher.voucher_number || voucher.id;
    const fileName = `${isRTL ? 'سند_صرف' : 'PaymentVoucher'}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('paymentVouchers.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  // Handle export all payment vouchers to Excel
  const handleExportAllToExcel = async () => {
    try {
      const allVouchers = await fetchAllPaymentVouchersForPDF();
      if (!allVouchers || allVouchers.length === 0) {
        toast.error(t('paymentVouchers.export.noData', { defaultValue: 'لا توجد بيانات' }), { rtl: isRTL });
        return;
      }

      const headerSerial = t('paymentVouchers.table.serialNumber', { defaultValue: '#' });
      const headerDate = t('paymentVouchers.table.date', { defaultValue: 'التاريخ' });
      const headerReference = t('paymentVouchers.table.reference', { defaultValue: 'المرجع' });
      const headerAccount = t('paymentVouchers.table.account', { defaultValue: 'الحساب' });
      const headerReceivedFrom = t('paymentVouchers.table.receivedFromName', { defaultValue: 'مستلم من' });
      const headerProject = t('paymentVouchers.table.project', { defaultValue: 'المشروع' });
      const headerAmount = t('paymentVouchers.table.amount', { defaultValue: 'المبلغ' });
      const headerStatus = t('paymentVouchers.table.status', { defaultValue: 'الحالة' });
      const headerCreatedBy = t('paymentVouchers.table.createdBy', { defaultValue: 'بواسطة' });
      const headerPostedDate = t('paymentVouchers.table.postedDate', { defaultValue: 'تاريخ الترحيل' });

      const sortedAllVouchers = sortVouchersList(allVouchers);
      const data = sortedAllVouchers.map((voucher) => ({
        [headerSerial]: voucher._serialNumber,
        [headerDate]: formatVoucherDate(voucher.voucher_date || voucher.date) || '—',
        [headerReference]: voucher.reference || voucher.voucher_number || voucher.id || '—',
        [headerAccount]: voucher.account
          ? `${voucher.account.code ? `${voucher.account.code} - ` : ''}${isRTL ? (voucher.account.name_ar || voucher.account.name || '') : (voucher.account.name_en || voucher.account.name || voucher.account.name_ar || '')}`
          : '—',
        [headerReceivedFrom]: getReceivedFromDisplayName(voucher, isRTL),
        [headerProject]: getProjectDisplayName(voucher, isRTL),
        [headerAmount]: Number(voucher.total_amount || voucher.amount || 0),
        [headerStatus]: statusLabel(voucher.status),
        [headerCreatedBy]: voucher.created_by || voucher.creator || '—',
        [headerPostedDate]: formatVoucherDate(voucher.posted_at) || '—',
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'سندات الصرف' : 'Payment Vouchers');
      const fileName = `${isRTL ? 'سندات_الصرف' : 'PaymentVouchers'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('paymentVouchers.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (err) {
      console.error('Error exporting payment vouchers to Excel:', err);
      toast.error(t('paymentVouchers.export.error', { defaultValue: 'فشل تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'posted':
        return t('paymentVouchers.status.posted');
      case 'draft':
        return t('paymentVouchers.status.draft');
      default:
        return status || '-';
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 'posted':
        return '#AC2000';
      case 'draft':
        return '#666';
      default:
        return '#0d6efd';
    }
  };

  const renderPagination = () => {
    if (!paymentVouchersPagination) return null;
    const current = paymentVouchersPagination.current_page || 1;
    const last = paymentVouchersPagination.last_page || 1;
    const pages = [];
    for (let i = 1; i <= last; i++) {
      if (i === 1 || i === last || (i >= current - 2 && i <= current + 2)) {
        pages.push(i);
      } else if (i === current - 3 || i === current + 3) {
        pages.push('...');
      }
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', paddingBottom: '20px' }}>
        <button
          onClick={() => setCurrentPage(Math.max(1, current - 1))}
          disabled={current === 1}
          style={{
            padding: '8px 12px',
            backgroundColor: current === 1 ? '#333' : '#AC2000',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: current === 1 ? 'not-allowed' : 'pointer',
          }}
        >
          {t('paymentVouchers.pagination.previous')}
        </button>
        {pages.map((p, idx) =>
          p === '...' ? (
            <span key={`ellipsis-${idx}`} style={{ padding: '8px', color: 'white' }}>...</span>
          ) : (
            <button
              key={p}
              onClick={() => setCurrentPage(p)}
              style={{
                padding: '8px 12px',
                backgroundColor: p === current ? '#AC2000' : '#1a1f2e',
                color: 'white',
                border: '1px solid #333',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => setCurrentPage(Math.min(last, current + 1))}
          disabled={current === last}
          style={{
            padding: '8px 12px',
            backgroundColor: current === last ? '#333' : '#AC2000',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: current === last ? 'not-allowed' : 'pointer',
          }}
        >
          {t('paymentVouchers.pagination.next')}
        </button>
      </div>
    );
  };

  if (isLoading && (!allPaymentVouchers || allPaymentVouchers.length === 0)) {
    return (
      <div
        style={{
          padding: '30px',
          backgroundColor: '#14263b',
          minHeight: 'calc(100vh - 80px)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          direction: dir
        }}
      >
        {t('paymentVouchers.loading')}
      </div>
    );
  }

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
      <h1 style={{ fontSize: '20px', margin: '0px 0 10px', textAlign }}>{t('paymentVouchers.title')}</h1>

      <div style={{ marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search', { defaultValue: 'بحث' })}
          style={{
            minWidth: '180px',
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid #333',
            backgroundColor: '#1a1f2e',
            color: 'white',
          }}
        />
        <div style={{ minWidth: '260px', position: 'relative' }} data-dropdown-container>
          <button
            type="button"
            onClick={() => setProjectDropdownOpen((prev) => !prev)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #333',
              backgroundColor: '#1a1f2e',
              color: 'white',
              textAlign: textAlign,
              cursor: 'pointer',
            }}
          >
            {renderProjectDisplay(projectId)}
          </button>
          {projectDropdownOpen && (
            <DropdownWithSearch
              isOpen={projectDropdownOpen}
              onToggle={setProjectDropdownOpen}
              searchTerm={projectSearchTerm}
              onSearchChange={handleProjectSearchChange}
              searchInputRef={projectSearchInputRef}
              items={projects || []}
              pagination={projectsPagination}
              currentPage={projectCurrentPage}
              onPageChange={setProjectCurrentPage}
              onSelect={(id) => {
                setProjectId(String(id));
                setProjectDropdownOpen(false);
              }}
              label={t('paymentVouchers.form.project', { defaultValue: 'المشروع' })}
            />
          )}
        </div>
        <div style={{ minWidth: '300px', position: 'relative' }} data-dropdown-container>
          <button
            type="button"
            onClick={() => setAccountDropdownOpen((prev) => !prev)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #333',
              backgroundColor: '#1a1f2e',
              color: 'white',
              textAlign: textAlign,
              cursor: 'pointer',
            }}
          >
            {renderAccountDisplay(accountId)}
          </button>
          {accountDropdownOpen && (
            <div style={{ position: 'absolute', top: '100%', marginTop: '6px', width: '100%', backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', zIndex: 30, maxHeight: '320px', overflow: 'auto' }}>
              <input
                type="text"
                value={accountSearchTerm}
                onChange={(e) => setAccountSearchTerm(e.target.value)}
                placeholder={t('paymentVouchers.form.searchAccount')}
                style={{ width: '100%', padding: '10px', border: 'none', borderBottom: '1px solid #374151', backgroundColor: '#111827', color: 'white' }}
              />
              {filteredAccounts.map((acc) => {
                const id = acc?.id ?? acc?.account_id;
                return (
                  <div
                    key={id}
                    onClick={() => {
                      setAccountId(String(id));
                      setAccountDropdownOpen(false);
                    }}
                    style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #2b3544' }}
                  >
                    {`${acc.code ? `${acc.code} - ` : ''}${isRTL ? (acc.name_ar || acc.name || acc.name_en || '') : (acc.name_en || acc.name || acc.name_ar || '')}`}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            minWidth: '140px',
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid #333',
            backgroundColor: '#1a1f2e',
            color: 'white',
          }}
        >
          <option value="">{t('common.all', { defaultValue: 'All' })}</option>
          <option value={STATUS_FILTER_ENUM.POSTED}>{t('paymentVouchers.status.posted')}</option>
          <option value={STATUS_FILTER_ENUM.DRAFT}>{t('paymentVouchers.status.draft')}</option>
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          style={{
            minWidth: '150px',
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid #333',
            backgroundColor: '#1a1f2e',
            color: 'white',
          }}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          style={{
            minWidth: '150px',
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid #333',
            backgroundColor: '#1a1f2e',
            color: 'white',
          }}
        />
        <button
          onClick={applyFilters}
          style={{
            backgroundColor: '#AC2000',
            border: 'none',
            color: 'white',
            padding: '10px 14px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          {t('common.apply', { defaultValue: 'Apply' })}
        </button>
        <button
          onClick={resetFilters}
          style={{
            backgroundColor: '#374151',
            border: 'none',
            color: 'white',
            padding: '10px 14px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          {t('common.reset', { defaultValue: 'Reset' })}
        </button>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
              {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setPerPageInput(Math.max(1, perPageInput - 1))}
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '6px 0 0 6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '32px'
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max="1000"
                value={perPageInput}
                onChange={(e) => setPerPageInput(Math.max(1, Math.min(parseInt(e.target.value) || 1, 1000)))}
                style={{
                  width: '70px',
                  padding: '6px 10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderRadius: 0,
                  color: 'white',
                  fontSize: '14px',
                  textAlign: 'center'
                }}
              />
              <button
                onClick={() => setPerPageInput(Math.min(1000, perPageInput + 1))}
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '0 6px 6px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '32px'
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: '#AC2000',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              {t('common.apply', { defaultValue: 'تطبيق' })}
            </button>
          </div>
          <button
            onClick={handlePreviewAll}
            disabled={!allPaymentVouchers || allPaymentVouchers.length === 0}
            style={{
              backgroundColor: !allPaymentVouchers || allPaymentVouchers.length === 0 ? '#666' : '#444',
              border: 'none',
              color: 'white',
              padding: '10px 14px',
              borderRadius: '8px',
              cursor: !allPaymentVouchers || allPaymentVouchers.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <FaEye /> {t('paymentVouchers.preview.all')}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf || !allPaymentVouchers || allPaymentVouchers.length === 0}
            style={{
              backgroundColor: isGeneratingPdf || !allPaymentVouchers || allPaymentVouchers.length === 0 ? '#666' : '#17a2b8',
              border: 'none',
              color: 'white',
              padding: '10px 14px',
              borderRadius: '8px',
              cursor: isGeneratingPdf || !allPaymentVouchers || allPaymentVouchers.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              opacity: isGeneratingPdf ? 0.6 : 1
            }}
          >
            <FaPrint /> {isGeneratingPdf ? t('paymentVouchers.print.generating') : t('paymentVouchers.print.all')}
          </button>
          <button
            onClick={handleExportAllToExcel}
            disabled={!allPaymentVouchers || allPaymentVouchers.length === 0}
            style={{
              backgroundColor: !allPaymentVouchers || allPaymentVouchers.length === 0 ? '#666' : '#28a745',
              border: 'none',
              color: 'white',
              padding: '10px 14px',
              borderRadius: '8px',
              cursor: !allPaymentVouchers || allPaymentVouchers.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <FaFileExcel /> {t('paymentVouchers.export.all')}
          </button>
          <button
            onClick={() => navigate('/payment-vouchers/add')}
            style={{
              backgroundColor: '#AC2000',
              border: 'none',
              color: 'white',
              padding: '10px 14px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <FaPlus /> {t('paymentVouchers.add')}
          </button>
        </div>
      </div>

      <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                {[
                  { label: t('paymentVouchers.table.serialNumber'), width: '60px', field: 'id' },
                  { label: t('paymentVouchers.table.date'), width: '120px', field: 'date' },
                  { label: t('paymentVouchers.table.reference'), width: '150px', field: 'reference' },
                  { label: t('paymentVouchers.table.account'), width: '220px', field: 'account' },
                  { label: t('paymentVouchers.table.receivedFromName', { defaultValue: 'مستلم من' }), width: '200px', field: 'received_from_name' },
                  { label: t('paymentVouchers.table.project', { defaultValue: 'المشروع' }), width: '180px', field: 'project' },
                  { label: t('paymentVouchers.table.amount'), width: '140px', field: 'amount' },
                  { label: t('paymentVouchers.table.status'), width: '120px', field: 'status' },
                  { label: t('paymentVouchers.table.createdBy'), width: '140px', field: 'created_by' },
                  { label: t('paymentVouchers.table.postedDate'), width: '160px', field: 'posted_date' },
                  { label: t('paymentVouchers.table.actions'), width: '220px' },
                ].map((h) => (
                  <th
                    key={h.label}
                    onClick={() => h.field && handleSort(h.field)}
                    style={{
                      padding: '18px 16px',
                      color: 'white',
                      borderBottom: '2px solid #333',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      width: h.width,
                      minWidth: h.width,
                      backgroundColor: '#AC2000',
                      cursor: h.field ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <span>{h.label}</span>
                      {h.field && sortField === h.field && (
                        <span>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                      )}
                      {h.field && sortField !== h.field && (
                        <span style={{ opacity: 0.3 }}>⇅</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(sortedPaymentVouchers || []).length === 0 ? (
                <tr>
                  <td
                    colSpan="11"
                    style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '16px',
                    }}
                  >
                    لا يوجد إيصال صرف نقدية متاح
                  </td>
                </tr>
              ) : (
                (sortedPaymentVouchers || []).map((voucher, idx) => {
                  const isDraft = voucher.status === 'draft';
                  const isPosted = voucher.status === 'posted';
                  const totalAmount = Number(voucher.total_amount || 0);
                  const currentPageSafe = paymentVouchersPagination?.current_page || currentPage || 1;

                  return (
                    <tr
                      key={voucher.id}
                      style={{
                        borderBottom: '1px solid #333',
                        backgroundColor: idx % 2 === 0 ? '#1b3350' : '#1a1f2e',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(ev) => {
                        const row = ev.target.closest('tr');
                        if (row) row.style.backgroundColor = '#2a3441';
                      }}
                      onMouseLeave={(ev) => {
                        const row = ev.target.closest('tr');
                        if (row) row.style.backgroundColor = idx % 2 === 0 ? '#1b3350' : '#1a1f2e';
                      }}
                    >
                      <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                        {voucher._serialNumber}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                        {formatVoucherDate(voucher.voucher_date) || '-'}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                        {convertArabicNumbersToEnglish(voucher.reference || '-')}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                        {voucher.account
                          ? `${convertArabicNumbersToEnglish(voucher.account.code ? `${voucher.account.code} - ` : '')}${isRTL ? (voucher.account.name_ar || voucher.account.name || '') : (voucher.account.name_en || voucher.account.name || voucher.account.name_ar || '')}`
                          : '-'}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                        {getReceivedFromDisplayName(voucher, isRTL)}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                        {getProjectDisplayName(voucher, isRTL)}
                      </td>
                      <td
                        style={{
                          padding: '18px 16px',
                          textAlign: 'center',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        {formatCurrency(totalAmount)}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center' }}>
                        <span
                          style={{
                            backgroundColor: statusColor(voucher.status),
                            color: 'white',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            display: 'inline-block',
                          }}
                        >
                          {statusLabel(voucher.status)}
                        </span>
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                        {convertArabicNumbersToEnglish(voucher.created_by || voucher.creator || '-')}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                        {formatVoucherDate(voucher.posted_at) || '-'}
                      </td>
                      <td style={{ padding: '18px 16px', textAlign: 'center' }}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                          }}
                        >
                          <button
                            title={t('paymentVouchers.actions.view')}
                            onClick={() => handlePreviewSingle(voucher)}
                            style={{
                              backgroundColor: '#666',
                              color: 'white',
                              border: 'none',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(ev) => {
                              ev.target.style.backgroundColor = '#777';
                              ev.target.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(ev) => {
                              ev.target.style.backgroundColor = '#666';
                              ev.target.style.transform = 'scale(1)';
                            }}
                          >
                            <FaEye />
                          </button>
                          {voucher.attachments && voucher.attachments.length > 0 && (
                            <button
                              title={t('paymentVouchers.actions.attachments', { defaultValue: 'Attachments' })}
                              onClick={() => openAttachmentsModal(voucher.attachments)}
                              style={{
                                backgroundColor: '#4b5563',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(ev) => {
                                ev.target.style.backgroundColor = '#6b7280';
                                ev.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(ev) => {
                                ev.target.style.backgroundColor = '#4b5563';
                                ev.target.style.transform = 'scale(1)';
                              }}
                            >
                              <FaPaperclip />
                            </button>
                          )}
                          <button
                            title={t('common.edit', { defaultValue: 'Edit' })}
                            onClick={() => handleEdit(voucher)}
                            style={{
                              backgroundColor: '#1f5ff1',
                              color: 'white',
                              border: 'none',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s',
                              marginLeft: '2px',
                            }}
                            onMouseEnter={(ev) => {
                              ev.target.style.backgroundColor = '#1741a8';
                              ev.target.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(ev) => {
                              ev.target.style.backgroundColor = '#1f5ff1';
                              ev.target.style.transform = 'scale(1)';
                            }}
                          >
                            <FaEdit />
                          </button>
                          <button
                            title={t('paymentVouchers.actions.print')}
                            onClick={() => handlePrintPaymentVoucher(voucher)}
                            disabled={isGeneratingPdf}
                            style={{
                              backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                              color: 'white',
                              border: 'none',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s',
                              opacity: isGeneratingPdf ? 0.6 : 1,
                            }}
                            onMouseEnter={(ev) => {
                              if (!isGeneratingPdf) {
                                ev.target.style.backgroundColor = '#138496';
                                ev.target.style.transform = 'scale(1.05)';
                              }
                            }}
                            onMouseLeave={(ev) => {
                              if (!isGeneratingPdf) {
                                ev.target.style.backgroundColor = '#17a2b8';
                                ev.target.style.transform = 'scale(1)';
                              }
                            }}
                          >
                            <FaPrint />
                          </button>
                          <button
                            title={t('paymentVouchers.actions.export')}
                            onClick={() => handleExportPaymentVoucherToExcel(voucher)}
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(ev) => {
                              ev.target.style.backgroundColor = '#218838';
                              ev.target.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(ev) => {
                              ev.target.style.backgroundColor = '#28a745';
                              ev.target.style.transform = 'scale(1)';
                            }}
                          >
                            <FaFileExcel />
                          </button>
                          {isDraft && (
                            <button
                              title={t('paymentVouchers.actions.post')}
                              onClick={() => handlePost(voucher.id)}
                              style={{
                                backgroundColor: '#845ef7',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(ev) => {
                                ev.target.style.backgroundColor = '#7950f2';
                                ev.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(ev) => {
                                ev.target.style.backgroundColor = '#845ef7';
                                ev.target.style.transform = 'scale(1)';
                              }}
                            >
                              <FaPaperPlane />
                            </button>
                          )}
                          {isDraft && (
                            <button
                              title={t('paymentVouchers.actions.delete')}
                              onClick={() => openDeleteModal(voucher)}
                              style={{
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(ev) => {
                                ev.target.style.backgroundColor = '#bb2d3b';
                                ev.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(ev) => {
                                ev.target.style.backgroundColor = '#dc3545';
                                ev.target.style.transform = 'scale(1)';
                              }}
                            >
                              <FaTrash />
                            </button>
                          )}
                          {isPosted && (
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>{t('paymentVouchers.posted')}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </div>

      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
          }}
        >
          <div
            style={{
              backgroundColor: '#1f2937',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '420px',
            }}
          >
            <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '18px', textAlign }}>{t('paymentVouchers.delete.title')}</h3>
            <p style={{ color: '#ccc', marginBottom: '8px', fontSize: '14px', textAlign }}>
              {t('paymentVouchers.delete.message')}
            </p>
            {voucherToDelete && (
              <div style={{ color: 'white', fontSize: '14px', marginBottom: '16px', textAlign }}>
                {t('paymentVouchers.tableHeaders.reference')}: {voucherToDelete.reference || '-'}
              </div>
            )}
            <p style={{ color: '#ef4444', marginBottom: '20px', fontSize: '12px', textAlign }}>{t('paymentVouchers.delete.warning')}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={closeDeleteModal}
                style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}
              >
                {t('paymentVouchers.delete.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}
              >
                {t('paymentVouchers.delete.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden PDF templates for Printing Vouchers */}
      {voucherForPrint && pdfType === 'single' && (
        <PaymentVoucherSingleTemplate
          voucher={voucherForPrint}
          isRTL={isRTL}
          formatDate={formatVoucherDate}
          formatCurrency={formatCurrency}
          statusLabel={statusLabel}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          isPreview={false}
          t={t}
        />
      )}

      {/* Dynamic measurement template for page splitting */}
      <PaymentVouchersPdfMeasurementTemplate
        paymentVouchers={allVouchersForPDF || []}
        isRTL={isRTL}
        t={t}
        formatDate={formatVoucherDate}
        formatCurrency={formatCurrency}
        statusLabel={statusLabel}
        activeFiltersText={activeFiltersForPdf}
        publicUrl={publicUrl}
      />

      {/* Hidden Print Templates for All Payment Vouchers - always rendered when chunks exist */}
      {/* FIX: Was `pdfType === 'all'` which was never set in handlePrintAll → templates never rendered → PDF empty */}
      {(pdfChunks || []).length > 0 && (pdfChunks || []).map((chunk, chunkIndex) => (
        <PaymentVouchersPdfPageTemplate
          key={chunkIndex}
          idPrefix="payment-vouchers-pdf-page"
          chunkIndex={chunkIndex}
          paymentVouchers={chunk.items}
          startIndex={chunk.startIndex}
          isRTL={isRTL}
          t={t}
          formatDate={formatVoucherDate}
          formatCurrency={formatCurrency}
          statusLabel={statusLabel}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          activeFiltersText={activeFiltersForPdf}
          isPreview={false}
          totalChunks={pdfChunks?.length || 1}
        />
      ))}

      {/* Preview Modal (unifies single and multiple preview) */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && (voucherForPreview || (pdfChunks && pdfChunks.length > 0))}
        title={voucherForPreview ? t('paymentVouchers.preview.single', { defaultValue: 'معاينة سند صرف' }) : t('paymentVouchers.title')}
        onClose={() => {
          setIsPreviewOpen(false);
          setVoucherForPreview(null);
          setPdfChunks([]);
        }}
        onPrint={voucherForPreview ? () => {
          setVoucherForPrint(voucherForPreview);
          setPdfType('single');
          handlePrintPaymentVoucher(voucherForPreview);
        } : handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        {voucherForPreview ? (
          <PaymentVoucherSingleTemplate
            voucher={voucherForPreview}
            isRTL={isRTL}
            formatDate={formatVoucherDate}
            formatCurrency={formatCurrency}
            statusLabel={statusLabel}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            isPreview={true}
            t={t}
          />
        ) : (
          (pdfChunks || []).map((chunk, chunkIndex) => (
            <PaymentVouchersPdfPageTemplate
              key={chunkIndex}
              idPrefix="preview-payment-vouchers-pdf-page"
              chunkIndex={chunkIndex}
              paymentVouchers={chunk.items}
              startIndex={chunk.startIndex}
              isRTL={isRTL}
              t={t}
              formatDate={formatVoucherDate}
              formatCurrency={formatCurrency}
              statusLabel={statusLabel}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              totalChunks={pdfChunks?.length || 1}
              activeFiltersText={activeFiltersForPdf}
              isPreview={true}
            />
          ))
        )}
      </PdfPreviewModal>

      {/* Attachments Modal (using portal, similar to ReceiptVouchers) */}
      {attachmentsModalOpen && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={closeAttachmentsModal}
        >
          <div
            style={{
              backgroundColor: '#1f2937',
              border: '1px solid #333',
              borderRadius: 12,
              padding: 20,
              width: '95%',
              maxWidth: 900,
              maxHeight: '90vh',
              overflow: 'auto',
              margin: 'auto',
              direction: dir,
              textAlign,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 16, color: 'white' }}>
              {t('paymentVouchers.attachmentsModal.title', { defaultValue: 'المرفقات' })}
            </h3>

            {selectedAttachments && Array.isArray(selectedAttachments) && selectedAttachments.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                {selectedAttachments.map((attachment, idx) => {
                  const fileUrl =
                    attachment?.file_url ||
                    attachment?.url ||
                    attachment?.media_url ||
                    (attachment?.path ? `${BASE_URL.slice(0, -3)}/storage/${attachment.path}` : '') ||
                    attachment?.full_url ||
                    attachment?.link ||
                    '';

                  const isImage =
                    (attachment.mime_type && attachment.mime_type.startsWith('image/')) ||
                    /\.(png|jpe?g|gif|webp)$/i.test(attachment.file_name || '');

                  return (
                    <div
                      key={attachment.id || idx}
                      style={{
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: 8,
                        padding: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      {isImage && fileUrl ? (
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '75%',
                            backgroundColor: '#14263b',
                            borderRadius: 4,
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={attachment.path ? `${BASE_URL.slice(0, -3)}/storage/${attachment.path}` : fileUrl}
                            alt={attachment.file_name || `Attachment ${idx + 1}`}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              cursor: 'pointer',
                            }}
                            onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const errorDiv = e.target.nextSibling;
                              if (errorDiv) errorDiv.style.display = 'flex';
                            }}
                          />
                          <div
                            style={{
                              display: 'none',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#888',
                              fontSize: 12,
                            }}
                          >
                            {t('paymentVouchers.attachmentsModal.imageError', { defaultValue: 'خطأ في تحميل الصورة' })}
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            paddingTop: '75%',
                            backgroundColor: '#14263b',
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#888',
                          }}
                        >
                          <FaFileAlt style={{ fontSize: 32 }} />
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 12,
                          color: '#ccc',
                          wordBreak: 'break-word',
                          textAlign: 'center',
                        }}
                      >
                        {attachment.file_name || attachment.name || `Attachment ${idx + 1}`}
                      </div>
                      {fileUrl && (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            backgroundColor: '#AC2000',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            textAlign: 'center',
                            textDecoration: 'none',
                            fontSize: 12,
                            fontWeight: 'bold',
                          }}
                        >
                          {isImage
                            ? t('paymentVouchers.attachmentsModal.view', { defaultValue: 'عرض' })
                            : t('paymentVouchers.attachmentsModal.download', { defaultValue: 'تحميل' })}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#ccc' }}>
                {t('paymentVouchers.attachmentsModal.noAttachments', { defaultValue: 'لا توجد مرفقات' })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
              <button
                onClick={closeAttachmentsModal}
                style={{
                  backgroundColor: '#4b5563',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                {t('paymentVouchers.attachmentsModal.close', { defaultValue: 'إغلاق' })}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// PDF Page Template for multiple vouchers
const PaymentVouchersPdfPageTemplate = ({ idPrefix, paymentVouchers, isRTL, t, formatDate, formatCurrency, statusLabel, publicUrl, headerLogo, chunkIndex = 0, totalChunks = 1, activeFiltersText = '', startIndex = 0, isPreview = true }) => {
  if (!paymentVouchers || paymentVouchers.length === 0) return null;

  const formatCurrencyEnglish = (amount) => convertArabicNumbersToEnglish(formatCurrency(amount));
  const formatDateEnglish = (date) => convertArabicNumbersToEnglish(formatDate(date));

  return (
    <div
      id={`${idPrefix}-${chunkIndex}`}
      aria-hidden={!isPreview}
      style={{
        position: isPreview ? 'relative' : 'absolute',
        left: isPreview ? 'auto' : '-9999px',
        top: isPreview ? 'auto' : 0,
        width: '794px',
        minHeight: '1123px',
        height: 'auto',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        margin: isPreview ? '0 auto' : 0,
        overflow: 'visible',
        marginBottom: isPreview && chunkIndex < totalChunks - 1 ? '20px' : 0
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
        height: 'auto',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0 }}>
                  {t('paymentVouchers.pdf.reportTitle', { defaultValue: isRTL ? 'تقرير إيصالات صرف نقدية' : 'Payment Vouchers Report' })}
                </h2>
                <p style={{ fontSize: '12px', color: '#333', margin: '4px 0 0' }}>
                  {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                {activeFiltersText && (
                  <div style={{ fontSize: '11px', color: '#444', margin: '4px 0 0', padding: '4px 8px', backgroundColor: 'rgba(0,0,128,0.05)', borderRadius: '4px', display: 'inline-block' }}>
                    {activeFiltersText}
                  </div>
                )}
              </div>
              <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '36px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('common.serialNumber', { defaultValue: '#' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('paymentVouchers.table.reference', { defaultValue: 'المرجع' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '85px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('paymentVouchers.table.date', { defaultValue: 'التاريخ' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '120px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('paymentVouchers.table.account', { defaultValue: 'الحساب' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '100px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('paymentVouchers.table.receivedFromName', { defaultValue: 'مستلم من' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '100px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('paymentVouchers.table.project', { defaultValue: 'المشروع' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', width: '120px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('paymentVouchers.pdf.statement', { defaultValue: 'البيان' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('paymentVouchers.table.amount', { defaultValue: 'المبلغ' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '60px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('paymentVouchers.table.status', { defaultValue: 'الحالة' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentVouchers.map((voucher, idx) => {
                      return (
                        <tr key={voucher.id || idx} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher._serialNumber}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher.reference || voucher.voucher_number || voucher.id || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatDateEnglish(voucher.voucher_date || voucher.date) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher.account ? `${voucher.account.code ? `${voucher.account.code} - ` : ''}${isRTL ? (voucher.account.name_ar || voucher.account.name || '') : (voucher.account.name_en || voucher.account.name || voucher.account.name_ar || '')}` : '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{getReceivedFromDisplayName(voucher, isRTL)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{getProjectDisplayName(voucher, isRTL)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle' }}>{voucher.notes || (voucher.details && voucher.details.length > 0 && voucher.details[0].description) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatCurrencyEnglish(voucher.total_amount || voucher.amount)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{statusLabel(voucher.status)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// PDF Measurement Template to compute pages dynamically using hidden DOM elements
const PaymentVouchersPdfMeasurementTemplate = ({ paymentVouchers, isRTL, t, formatDate, formatCurrency, statusLabel, activeFiltersText = '', publicUrl = '' }) => {
  if (!paymentVouchers || paymentVouchers.length === 0) return null;

  const formatCurrencyEnglish = (amount) => convertArabicNumbersToEnglish(formatCurrency(amount));
  const formatDateEnglish = (date) => convertArabicNumbersToEnglish(formatDate(date));

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        width: '794px',
        minHeight: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'visible'
      }}
    >
      <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', minHeight: '1043px' }}>
        <div style={{ border: '2px solid #000', minHeight: '1033px', height: '1033px', padding: '18px 18px 10px', position: 'relative', display: 'flex', flexDirection: 'column', direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#ffffff' }}>
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <div id="payment-vouchers-pdf-measure-top-header" style={{ display: 'flex', flexDirection: 'column' }}>
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px',
                  borderBottom: '4px double #000',
                  paddingBottom: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
              <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0 }}>
                  {t('paymentVouchers.pdf.reportTitle', { defaultValue: isRTL ? 'تقرير إيصالات صرف نقدية' : 'Payment Vouchers Report' })}
                </h2>
                <p style={{ fontSize: '12px', color: '#333', margin: '4px 0 0' }}>
                  {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                {activeFiltersText && (
                  <div style={{ fontSize: '11px', color: '#444', margin: '4px 0 0', padding: '4px 8px', backgroundColor: 'rgba(0,0,128,0.05)', borderRadius: '4px', display: 'inline-block' }}>
                    {activeFiltersText}
                  </div>
                )}
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif' }}>
              <thead id="payment-vouchers-pdf-measure-thead">
                <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '36px' }}>{t('common.serialNumber', { defaultValue: '#' })}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '80px' }}>{t('paymentVouchers.table.reference', { defaultValue: 'المرجع' })}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '85px' }}>{t('paymentVouchers.table.date', { defaultValue: 'التاريخ' })}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '120px' }}>{t('paymentVouchers.table.account', { defaultValue: 'الحساب' })}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '100px' }}>{t('paymentVouchers.table.receivedFromName', { defaultValue: 'مستلم من' })}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '100px' }}>{t('paymentVouchers.table.project', { defaultValue: 'المشروع' })}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', width: '120px' }}>{t('paymentVouchers.pdf.statement', { defaultValue: 'البيان' })}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '80px' }}>{t('paymentVouchers.table.amount', { defaultValue: 'المبلغ' })}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '60px' }}>{t('paymentVouchers.table.status', { defaultValue: 'الحالة' })}</th>
                </tr>
              </thead>
              <tbody id="payment-vouchers-pdf-measure-tbody">
                {paymentVouchers.map((voucher, idx) => (
                  <tr key={voucher.id || idx}>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher._serialNumber}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher.reference || voucher.voucher_number || voucher.id || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatDateEnglish(voucher.voucher_date || voucher.date) || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher.account ? `${voucher.account.code ? `${voucher.account.code} - ` : ''}${isRTL ? (voucher.account.name_ar || voucher.account.name || '') : (voucher.account.name_en || voucher.account.name || voucher.account.name_ar || '')}` : '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{getReceivedFromDisplayName(voucher, isRTL)}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{getProjectDisplayName(voucher, isRTL)}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle' }}>{voucher.notes || (voucher.details && voucher.details.length > 0 && voucher.details[0].description) || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatCurrencyEnglish(voucher.total_amount || voucher.amount)}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{statusLabel(voucher.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Footer reserve: matches paddingBottom: '120px' in page template content area */}
            <div id="payment-vouchers-pdf-measure-footer" style={{ height: '120px', flexShrink: 0 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// PDF Single Voucher Template
const PaymentVoucherSingleTemplate = ({ voucher, isRTL, formatDate, formatCurrency, statusLabel, publicUrl, headerLogo, isPreview = false, t }) => {
  if (!voucher) return null;

  const formatCurrencyEnglish = (amount) => convertArabicNumbersToEnglish(formatCurrency(amount));
  const formatDateEnglish = (date) => convertArabicNumbersToEnglish(formatDate(date));

  const details = Array.isArray(voucher.details) ? voucher.details : [];
  const totalAmount = Number(voucher.amount || voucher.total_amount || details.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0));
  const accountName = voucher.account ? (isRTL ? (voucher.account.name_ar || voucher.account.name) : (voucher.account.name_en || voucher.account.name_ar || voucher.account.name)) : '';
  const accountCode = voucher.account?.code || '';

  return (
    <div
      id={isPreview ? `preview-payment-voucher-pdf-${voucher.id}` : `payment-voucher-pdf-${voucher.id}`}
      aria-hidden={!isPreview}
      style={{
        position: isPreview ? 'relative' : 'absolute',
        left: isPreview ? 'auto' : '-9999px',
        top: isPreview ? 'auto' : 0,
        width: '794px',
        minHeight: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left',
          backgroundColor: '#ffffff',
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />

          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '200px' }}>
              <div style={{ textAlign: 'center', margin: '20px 0 30px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '900', color: '#000', margin: 0 }}>
                  {t('paymentVouchers.pdf.paymentVoucher', { defaultValue: 'سند صرف نقدية' })}
                </h2>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '16px' }}>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <span style={{ minWidth: '120px' }}>{t('paymentVouchers.pdf.reference', { defaultValue: 'المرجع' })}:</span>
                  <span style={{ borderBottom: '1px dotted #000', padding: '0 10px', minWidth: '150px' }}>{voucher.reference || voucher.voucher_number || voucher.id || '—'}</span>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <span>{t('paymentVouchers.pdf.date', { defaultValue: 'التاريخ' })}:</span>
                  <span style={{ borderBottom: '1px dotted #000', padding: '0 10px', minWidth: '150px' }}>{formatDateEnglish(voucher.voucher_date)}</span>
                </div>
              </div>

              <div style={{ marginBottom: '20px', fontSize: '16px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', textDecoration: 'underline' }}>{t('paymentVouchers.pdf.deliveredAccount', { defaultValue: 'المنصرف له' })}:</div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <span style={{ borderBottom: '1px dotted #000', flex: 1 }}>
                    {accountCode ? `${accountCode} - ` : ''} {accountName || '—'}
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '15px', fontSize: '16px' }}>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '5px' }}>
                    <span>{t('paymentVouchers.pdf.receivedFrom', { defaultValue: 'مستلم من' })}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: details.length > 0 ? '8px' : '0' }}>
                    <span style={{ borderBottom: '1px dotted #000', padding: '0 10px', flex: 1 }}>
                      {getReceivedFromDisplayName(voucher, isRTL)}
                    </span>
                  </div>
                </div>

                <div style={{ marginBottom: '15px', fontSize: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span>{t('paymentVouchers.pdf.amount', { defaultValue: 'المبلغ' })}:</span>
                  <span style={{
                    border: '1px solid #000',
                    padding: '5px 15px',
                    flex: 1,
                    fontWeight: 'bold',
                    backgroundColor: '#f9f9f9'
                  }}>
                    ({formatCurrencyEnglish(totalAmount)} {t('paymentVouchers.pdf.only', { defaultValue: 'فقط لا غير' })})
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '16px', textDecoration: 'underline' }}>{t('paymentVouchers.pdf.deliveryDetails', { defaultValue: 'تفاصيل الصرف' })}:</div>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  border: '1px solid #000',
                  fontSize: '15px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', width: '70%' }}>{t('paymentVouchers.pdf.statement', { defaultValue: 'البيان' })}</th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', width: '30%' }}>{t('paymentVouchers.pdf.amount', { defaultValue: 'المبلغ' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(details.length > 0 ? details : [{ description: voucher.notes || 'تسليم نقدية', amount: totalAmount }]).map((d, i) => (
                      <tr key={i} style={{ height: '30px' }}>
                        <td style={{ border: '1px solid #000', padding: '5px 10px' }}>{d.description || (d.account && (isRTL ? d.account.name_ar : d.account.name)) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '5px 10px', textAlign: 'center' }}>{formatCurrencyEnglish(d.amount)}</td>
                      </tr>
                    ))}
                    <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{t('paymentVouchers.pdf.totalAmount', { defaultValue: 'الإجمالي' })}</td>
                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{formatCurrencyEnglish(totalAmount)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ marginBottom: '20px', fontSize: '16px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', textDecoration: 'underline' }}>{t('paymentVouchers.pdf.deliveryMethod', { defaultValue: 'طريقة الصرف' })}:</div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <span>{t('paymentVouchers.pdf.through', { defaultValue: 'عن طريق' })}:</span>
                  <span>
                    {voucher.treasury
                      ? `${convertArabicNumbersToEnglish(voucher.treasury.code || '')} - ${isRTL ? (voucher.treasury.name_ar || voucher.treasury.name) : (voucher.treasury.name || voucher.treasury.name_ar)}`
                      : t('paymentVouchers.pdf.cash', { defaultValue: 'نقداً' })}
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '20px', fontSize: '16px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{t('paymentVouchers.pdf.notes', { defaultValue: 'ملاحظات' })}:</div>
                <div style={{
                  border: '1px solid #000',
                  minHeight: '60px',
                  padding: '10px',
                  backgroundColor: '#f9f9f9',
                  whiteSpace: 'pre-wrap'
                }}>
                  {voucher.notes || '—'}
                </div>
              </div>

              <div style={{ marginTop: 'auto', paddingTop: '10px' }}>
                <div style={{
                  border: '1px solid #000',
                  padding: '15px',
                  fontSize: '15px'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '15px', textDecoration: 'underline' }}>{t('paymentVouchers.pdf.approvals', { defaultValue: 'التوقيعات والاعتمادات' })}</div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <span>{t('paymentVouchers.pdf.status', { defaultValue: 'الحالة' })}:</span>
                      <span style={{ fontWeight: 'bold' }}>{statusLabel(voucher.status)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <span>{t('paymentVouchers.pdf.createdBy', { defaultValue: 'منشئ السند' })}:</span>
                      <span>{voucher.created_by || 'Admin'}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0' }}>
                    <div style={{ width: '45%' }}>
                      <div style={{ marginBottom: '5px' }}>
                        <span>{t('paymentVouchers.pdf.delivererName', { defaultValue: 'المسلم' })}:</span> .....................................................
                      </div>
                    </div>
                    <div style={{ width: '45%' }}>
                      <div style={{ marginBottom: '5px' }}>
                        <span>{t('paymentVouchers.pdf.receiverName', { defaultValue: 'المستلم' })}:</span> .....................................................
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label, renderItem }) => {
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';

  const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = isRTL
      ? (item[nameArField] || item[nameEnField] || '')
      : (item[nameEnField] || item[nameArField] || '');
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const internalInputRef = useRef(null);
  const inputRef = searchInputRef || internalInputRef;

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, inputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
      <div style={{ padding: 8 }}>
        <input
          ref={inputRef}
          type="text"
          placeholder={`${t('quotations.add.search', { defaultValue: 'ابحث عن' })} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              inputRef.current?.focus({ preventScroll: true });
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
        />
      </div>
      {pagination && pagination.last_page >= 1 && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('common.pagination.page', { defaultValue: 'الصفحة' })} {pagination.current_page} {t('common.pagination.of', { defaultValue: 'من' })} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div>
        {(Array.isArray(items) ? items : []).map((item) => (
          <div
            key={item.id}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelect(item.id);
            }}
            style={{
              padding: '12px 15px',
              cursor: 'pointer',
              borderTop: '1px solid #333',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'white',
              fontSize: '14px',
              transition: 'background-color 0.2s',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#14263b'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span style={{ color: 'white' }}>
              {renderItem ? renderItem(item) : renderDropdownItemLabel(item, 'code', 'name_ar', 'name_en')}
            </span>
          </div>
        ))}
        {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: isRTL ? 'rtl' : 'ltr' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>}
      </div>
    </div>
  );
};

export default PaymentVouchers;
